import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  alpha,
  Stack,
} from '@mui/material';
import {
  Lock as LockIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import API from '../config';
import api from '../../services/api';

export default function BlurOverlay({ 
  onSubscribe, 
  title = "Subscribe to OnlyStats VIP",
  subtitle = "to see all creators",
  showCount = true,
  lockedCount = 0,
  totalCount = 0,
  message = null,
  customBenefits = null,
  centerBenefits = true
}) {
  const theme = useTheme();
  const { authData } = useContext(AuthContext);
  const [monthlyPrice, setMonthlyPrice] = useState(1.00); // Default fallback
  const [loadingPrice, setLoadingPrice] = useState(true);

  // Fetch the current monthly price
  useEffect(() => {
    const fetchPrice = async () => {
      if (!authData?.access_token) {
        setLoadingPrice(false);
        return;
      }
      
      try {
        const response = await api.get(`${API}/api/subscription/plans`);
        const plans = response.data.plans || [];
        const monthlyPlan = plans.find(p => p.interval === 'month');
        if (monthlyPlan) {
          setMonthlyPrice(monthlyPlan.unitAmount / 100);
        }
      } catch (error) {
        console.error('Error fetching subscription price:', error);
        // Keep default price on error
      } finally {
        setLoadingPrice(false);
      }
    };

    fetchPrice();
  }, [authData]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
        backdropFilter: 'blur(8px)',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        p: 4,
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `radial-gradient(circle at 25% 25%, ${theme.palette.primary.main} 2px, transparent 2px),
                           radial-gradient(circle at 75% 75%, ${theme.palette.secondary.main} 2px, transparent 2px)`,
          backgroundSize: '50px 50px',
          backgroundPosition: '0 0, 25px 25px',
        }}
      />
      
      <Box
        sx={{
          textAlign: 'center',
          p: 4,
          maxWidth: 400,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Lock Icon */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            mb: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          {title}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h6"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 3, fontWeight: 400 }}
        >
          {subtitle}
        </Typography>

        {/* Count Display */}
        {showCount && lockedCount > 0 && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              mb: 3,
            }}
          >
            <StarIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="body1" fontWeight={600} color="primary.main">
              {message || `${lockedCount} more creator${lockedCount !== 1 ? 's' : ''} available`}
            </Typography>
          </Box>
        )}

        {/* Benefits List */}
        <Stack spacing={1} sx={{ mb: 4, textAlign: centerBenefits ? 'center' : 'left', alignItems: centerBenefits ? 'center' : 'flex-start' }}>
          {customBenefits ? (
            customBenefits.map((benefit, index) => (
              <Typography key={index} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: centerBenefits ? 'center' : 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                {benefit}
              </Typography>
            ))
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: centerBenefits ? 'center' : 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                Access to 1000+ verified creators
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: centerBenefits ? 'center' : 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                Real subscriber reviews & ratings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: centerBenefits ? 'center' : 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                VIP leaderboard access
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: centerBenefits ? 'center' : 'flex-start' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.primary.main }} />
                Save money on bad subscriptions
              </Typography>
            </>
          )}
        </Stack>

        {/* Subscribe Button */}
        <Button
          variant="contained"
          size="large"
          onClick={onSubscribe}
          sx={{
            py: 2,
            px: 4,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 3,
            textTransform: 'none',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
              boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {loadingPrice ? (
            'Subscribe to VIP'
          ) : (
            `Subscribe to VIP - Only $${monthlyPrice.toFixed(2)}/month`
          )}
        </Button>

        {/* Small Print */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Cancel anytime • 30-day money-back guarantee
        </Typography>
      </Box>
    </Box>
  );
}
